<template>
  <div class="gs-asset-type gs-banners">
    <div class="gs-banners__grid">
      <article
        v-for="(banner, bannerIndex) in banners"
        :key="banner.id"
        class="gs-banner-card"
      >
        <div class="gs-banner-card__well">
          <img v-if="banner.image" class="gs-banner-card__image" :src="banner.image" :alt="banner.name" />
          <gs-icon v-else name="image" size="24"></gs-icon>
        </div>
        <div class="gs-banner-card__foot">
          <span class="gs-banner-card__text">
            <span class="gs-banner-card__name">{{ banner.name || 'Untitled banner' }}</span>
            <span class="gs-banner-card__page">{{ landingPageName(banner.landingPage) }}</span>
          </span>
          <button
            type="button"
            class="gs-banner-card__delete"
            data-action="delete-banner"
            title="Delete banner"
            aria-label="Delete banner"
            @click="confirmDeleteBanner(bannerIndex)"
          >
            <trash-icon class="w-4 h-4 fill-current" />
          </button>
        </div>
      </article>

      <button type="button" class="gs-banner-add" data-action="add-banner" @click="createNewBanner()">
        <gs-icon name="plus" size="20"></gs-icon>
        <span>{{ $t('frontend_resources_add_new_banner') }}</span>
      </button>
    </div>

    <p v-if="!hasBanners" class="gs-banners__hint">
      {{ $t('frontend_resources_you_currently_dont_have') }}
    </p>

    <gs-drawer
      v-model="addOpen"
      size="lg"
      :title="$t('frontend_resources_add_new_banner')"
      subtitle="Affiliates see this image and link in their promo tools."
    >
      <div class="gs-banner-drawer">
        <gs-field :label="$t('frontend_resources_name')">
          <text-input
            v-model="addedBannerName"
            :placeholder="$t('frontend_resources_name')"
          />
        </gs-field>

        <gs-field :label="$t('frontend_resources_link_to')">
          <gs-name-select
            v-model="addedBannerLandingPageId"
            size="full"
            max-height="220px"
            :options="landingPagesOptions"
          />
        </gs-field>

        <gs-field :label="$t('frontend_resources_add_banner')">
          <div class="gs-banner-drawer__picker">
            <image-picker v-model="addedBannerImage" :primary="true"></image-picker>
            <img
              v-if="addedBannerImage"
              class="gs-banner-drawer__preview"
              :src="addedBannerImage"
              alt="Selected banner"
            />
          </div>
        </gs-field>
      </div>

      <div class="gs-banner-drawer__footer">
        <gs-button variant="outline" @click="closeAddBanner()">
          {{ $t('frontend_cancel') }}
        </gs-button>
        <gs-button variant="primary" :disabled="!canAddBanner" @click="addBanner()">
          {{ $t('frontend_resources_add_banner') }}
        </gs-button>
      </div>
    </gs-drawer>

    <gs-alert-dialog
      v-model="confirmOpen"
      title="Delete this banner?"
      :copy="$t('frontend_non_revertable')"
      :confirm-label="$t('frontend_delete_confirm')"
      @confirm="deleteBanner()"
    />
  </div>
</template>

<script>
import generateNumberMixin from '@/mixins/generateNumberMixin';
import ImagePicker from '@groovepages/gd-ui-kit/components/ImagePickerModal';
import TrashIcon from '@/assets/images/icons/TrashIcon.svg';

export default {
  mixins: [generateNumberMixin],
  components: {
    ImagePicker,
    TrashIcon,
  },
  props: {
    funnelResource: {
      type: Object,
      required: true,
    },
    landingPages: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      localFunnelResource: this.funnelResource,
      // Drives the "Add banner" drawer; the legacy `gd-modal` was opened over
      // the root event bus, the drawer is plain component state.
      addOpen: false,
      confirmOpen: false,
      bannerToDeleteIndex: null,
      addedBannerName: '',
      addedBannerLandingPageId: '',
      addedBannerImage: '',
    };
  },
  computed: {
    banners() {
      return (this.localFunnelResource && this.localFunnelResource.banners) || [];
    },
    landingPagesOptions() {
      return this.landingPages.map((landingPage) => {
        return {
          value: landingPage.id,
          text: landingPage.description,
        };
      });
    },
    hasBanners() {
      return (
        this.localFunnelResource &&
        this.localFunnelResource.banners &&
        this.localFunnelResource.banners.length
      );
    },
    canAddBanner() {
      return (
        this.addedBannerImage != '' &&
        this.addedBannerLandingPageId &&
        this.addedBannerLandingPageId != '' &&
        this.addedBannerLandingPageId > 0 &&
        this.addedBannerName &&
        this.addedBannerName != ''
      );
    },
  },
  methods: {
    landingPageName(landingPageId) {
      const landingPage = this.landingPages.filter(
        (page) => String(page.id) === String(landingPageId)
      )[0];

      return landingPage ? landingPage.description : 'No landing page';
    },
    updateFunnelResource() {
      this.$emit('updateFunnelResource', this.localFunnelResource);
    },
    createNewBanner() {
      this.addedBannerImage = '';
      this.addedBannerName = '';
      if (this.landingPages.length) {
        this.addedBannerLandingPageId = this.landingPages[0].id;
      }

      this.addOpen = true;
    },
    closeAddBanner() {
      this.addOpen = false;
    },
    addBanner() {
      const newPostId = this.generateNumber();
      this.localFunnelResource.banners.push({
        id: newPostId,
        name: this.addedBannerName,
        image: this.addedBannerImage,
        landingPage: this.addedBannerLandingPageId,
      });

      this.updateFunnelResource();
      this.addOpen = false;
    },
    confirmDeleteBanner(index) {
      this.bannerToDeleteIndex = index;
      this.confirmOpen = true;
    },
    deleteBanner() {
      this.localFunnelResource.banners.splice(this.bannerToDeleteIndex, 1);
      this.updateFunnelResource();
    },
  }
}
</script>

<!-- `.gs-asset-type` comes from the shared asset stylesheet; pulled in here
     so this tab does not depend on a sibling tab having loaded it. -->
<style src="./asset-split.css"></style>

<style scoped>
.gs-banners {
  min-width: 0;
}

.gs-banners__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  min-width: 0;
}

.gs-banners__hint {
  margin: 16px 0 0;
  max-width: 62ch;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.55;
}

.gs-banner-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  padding: 14px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-banner-card__well {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 160px;
  padding: 10px;
  color: var(--gs-muted-foreground);
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-md);
}

.gs-banner-card__image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.gs-banner-card__foot {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
}

.gs-banner-card__text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.gs-banner-card__name {
  overflow: hidden;
  color: var(--gs-foreground);
  font-size: 15px;
  font-weight: 650;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-banner-card__page {
  overflow: hidden;
  color: var(--gs-muted-foreground);
  font-size: 13px;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gs-banner-card__delete {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  color: var(--gs-danger);
  background: transparent;
  border: 0;
  border-radius: var(--gs-radius-sm);
  cursor: pointer;
  transition: background-color 120ms ease;
}

.gs-banner-card__delete:hover {
  background: var(--gs-muted);
}

.gs-banner-card__delete:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-banner-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 220px;
  padding: 16px;
  color: var(--gs-primary);
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  background: var(--gs-primary-soft);
  border: 1px dashed var(--gs-primary);
  border-radius: var(--gs-radius-lg);
  cursor: pointer;
  transition: background-color 120ms ease;
}

.gs-banner-add:hover {
  background: var(--gs-surface);
}

.gs-banner-add:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-banner-drawer {
  display: flex;
  flex-direction: column;
  gap: 18px;
  min-width: 0;
}

.gs-banner-drawer__picker {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.gs-banner-drawer__preview {
  max-width: 220px;
  max-height: 120px;
  object-fit: contain;
  background: var(--gs-surface-subtle);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-sm);
}

.gs-banner-drawer__footer {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--gs-border);
}
</style>
